import VataCopy from "../../assets/Vata.png";
import Pitta from "../../assets/Pitta.png";
import Kapha from "../../assets/Kapha.png";
import VataPitta from "../../assets/Vata-Pitta.png";
import VataKapha from "../../assets/Kapha-Vata.png";
import VataPittaKapha from "../../assets/Vata-Pitta-Kapha.png";
import PittaKapha from "../../assets/Pitta-Kapha.png";
import heic2any from "heic2any";

import { PDFDocument } from "pdf-lib";

// Phone number parsing utility
export interface ParsedPhoneNumber {
  countryCode: string;
  phoneNumber: string;
  fullPhoneNumber: string;
}

export const parsePhoneNumber = (phoneNumber: string): ParsedPhoneNumber => {
  if (!phoneNumber) {
    return { countryCode: "", phoneNumber: "", fullPhoneNumber: "" };
  }

  // If it's an Indian number (+91)
  if (phoneNumber.startsWith("+91")) {
    return {
      countryCode: "+91",
      phoneNumber: phoneNumber.substring(3),
      fullPhoneNumber: phoneNumber,
    };
  }

  // If it starts with + (other international numbers)
  if (phoneNumber.startsWith("+")) {
    // Common country codes to try in order (longest first)
    const countryCodes = [
      "+1",
      "+7",
      "+20",
      "+27",
      "+30",
      "+31",
      "+32",
      "+33",
      "+34",
      "+36",
      "+39",
      "+40",
      "+41",
      "+43",
      "+44",
      "+45",
      "+46",
      "+47",
      "+48",
      "+49",
      "+51",
      "+52",
      "+53",
      "+54",
      "+55",
      "+56",
      "+57",
      "+58",
      "+60",
      "+61",
      "+62",
      "+63",
      "+64",
      "+65",
      "+66",
      "+81",
      "+82",
      "+84",
      "+86",
      "+90",
      "+91",
      "+92",
      "+93",
      "+94",
      "+95",
      "+98",
      "+212",
      "+213",
      "+216",
      "+218",
      "+220",
      "+221",
      "+222",
      "+223",
      "+224",
      "+225",
      "+226",
      "+227",
      "+228",
      "+229",
      "+230",
      "+231",
      "+232",
      "+233",
      "+234",
      "+235",
      "+236",
      "+237",
      "+238",
      "+239",
      "+240",
      "+241",
      "+242",
      "+243",
      "+244",
      "+245",
      "+246",
      "+248",
      "+249",
      "+250",
      "+251",
      "+252",
      "+253",
      "+254",
      "+255",
      "+256",
      "+257",
      "+258",
      "+260",
      "+261",
      "+262",
      "+263",
      "+264",
      "+265",
      "+266",
      "+267",
      "+268",
      "+269",
      "+290",
      "+291",
      "+297",
      "+298",
      "+299",
      "+350",
      "+351",
      "+352",
      "+353",
      "+354",
      "+355",
      "+356",
      "+357",
      "+358",
      "+359",
      "+370",
      "+371",
      "+372",
      "+373",
      "+374",
      "+375",
      "+376",
      "+377",
      "+378",
      "+380",
      "+381",
      "+382",
      "+383",
      "+385",
      "+386",
      "+387",
      "+389",
      "+420",
      "+421",
      "+423",
      "+500",
      "+501",
      "+502",
      "+503",
      "+504",
      "+505",
      "+506",
      "+507",
      "+508",
      "+509",
      "+590",
      "+591",
      "+592",
      "+593",
      "+594",
      "+595",
      "+596",
      "+597",
      "+598",
      "+599",
      "+670",
      "+672",
      "+673",
      "+674",
      "+675",
      "+676",
      "+677",
      "+678",
      "+679",
      "+680",
      "+681",
      "+682",
      "+683",
      "+684",
      "+685",
      "+686",
      "+687",
      "+688",
      "+689",
      "+690",
      "+691",
      "+692",
      "+850",
      "+852",
      "+853",
      "+855",
      "+856",
      "+880",
      "+886",
      "+960",
      "+961",
      "+962",
      "+963",
      "+964",
      "+965",
      "+966",
      "+967",
      "+968",
      "+970",
      "+971",
      "+972",
      "+973",
      "+974",
      "+975",
      "+976",
      "+977",
      "+992",
      "+993",
      "+994",
      "+995",
      "+996",
      "+998",
    ];

    // Try to match known country codes (longest first)
    for (const code of countryCodes) {
      if (phoneNumber.startsWith(code)) {
        return {
          countryCode: code,
          phoneNumber: phoneNumber.substring(code.length),
          fullPhoneNumber: phoneNumber,
        };
      }
    }

    // Fallback: if no known country code matches, try generic 1-3 digit pattern
    const match = phoneNumber.match(/^\+(\d{1,3})(.*)$/);
    if (match) {
      return {
        countryCode: `+${match[1]}`,
        phoneNumber: match[2],
        fullPhoneNumber: phoneNumber,
      };
    }
  }

  // If no + prefix, assume it's a local number
  return {
    countryCode: "",
    phoneNumber: phoneNumber,
    fullPhoneNumber: phoneNumber,
  };
};

// Get clinic user ID based on the new rules
export const getClinicUserId = (phoneNumber: string): string => {
  if (!phoneNumber || phoneNumber.trim() === "") {
    throw new Error("Phone number is required");
  }

  const parsed = parsePhoneNumber(phoneNumber);

  // Rule 1: If Indian (+91), remove +91 and save
  if (parsed.countryCode === "+91") {
    if (!parsed.phoneNumber || parsed.phoneNumber.trim() === "") {
      throw new Error("Invalid Indian phone number format");
    }
    return parsed.phoneNumber;
  }

  // Rule 2: For all other international numbers, keep complete number with +
  if (parsed.countryCode) {
    if (!parsed.fullPhoneNumber || parsed.fullPhoneNumber.trim() === "") {
      throw new Error("Invalid international phone number format");
    }
    return parsed.fullPhoneNumber;
  }

  // If no country code, return as is (local number)
  if (!parsed.phoneNumber || parsed.phoneNumber.trim() === "") {
    throw new Error("Invalid local phone number format");
  }
  return parsed.phoneNumber;
};

// Logic function to determine the question details
export const determineQuestionDetails = (
  currentQuestions: any,
  sectionIndex: number,
  questionIndex: number
) => {
  const section = currentQuestions[sectionIndex];
  const currentQuestion = section[questionIndex];
  const nextQuestion = section[questionIndex + 1];
  const sectionLength = section.length;

  let questionSection = "normal";

  if (sectionLength > 1) {
    if (nextQuestion?.attributes.dependent_question_id) {
      questionSection = "dependent";
    } else if (
      currentQuestion.attributes.question_text ===
      nextQuestion?.attributes.question_text
    ) {
      questionSection = "sub";
    }
  } else {
    questionSection =
      currentQuestion.attributes.option_type === "Chips" ? "sub" : "normal";
  }

  return {
    questionTypes: currentQuestion.attributes.option_type,
    sectionLength,
    questionSection,
  };
};

// Logic function to handle first load
export const handleFirstLoad = (currentQuestions: any) => {
  const sectionIndex = 0;
  const questionIndex = 0;

  const questionDetails = determineQuestionDetails(
    currentQuestions,
    sectionIndex,
    questionIndex
  );

  return { sectionIndex, questionIndex, ...questionDetails };
};

// Logic function to handle forward navigation
export const handleForwardNavigation = (
  currentQuestions: any,
  sectionIndex: number
) => {
  const newSectionIndex = sectionIndex + 1;
  const questionIndex = 0;

  const questionDetails = determineQuestionDetails(
    currentQuestions,
    newSectionIndex,
    questionIndex
  );

  return { sectionIndex: newSectionIndex, questionIndex, ...questionDetails };
};

// Logic function to handle backward navigation
export const handleBackwardNavigation = (
  currentQuestions: any,
  sectionIndex: number
) => {
  const newSectionIndex = sectionIndex - 1;
  const questionIndex = currentQuestions[newSectionIndex].length - 1;

  const questionDetails = determineQuestionDetails(
    currentQuestions,
    newSectionIndex,
    questionIndex
  );

  return { sectionIndex: newSectionIndex, questionIndex, ...questionDetails };
};

export const isOptionselected = (optionSelected: any, question_id: string) => {
  return optionSelected[question_id] !== undefined
    ? optionSelected[question_id]
    : undefined;
};

export const getDependentQuestionIndex = (
  section: any,
  optionSelected: any,
  startingQuestionIndex: number
) => {
  try {
    // Get the option selcted of first question in thr dependent question list
    let firstSlectedOptionValues = [];
    for (
      let j = 0;
      j < optionSelected[section[0].attributes.question_id].length;
      j++
    ) {
      let option = optionSelected[section[0].attributes.question_id][j];
      if (option !== -1) {
        firstSlectedOptionValues.push(
          section[0].attributes.question_values[option]
        );
      }
    }

    // get the index of the dependent question
    for (let i = startingQuestionIndex - 1; i > 0; i--) {
      if (
        firstSlectedOptionValues.includes(
          section[i].attributes.dependent_question_value
        )
      ) {
        return i;
      }
    }

    // if no question found then return 0
    return 0;
  } catch (e) {
    console.error(e);
    throw new Error("Error in getDependentQuestionIndex");
  }
};

export const getQuestionSectionType = (section: any) => {
  try {
    if (section[section.length - 1].attributes.option_type === "Chips") {
      return "sub";
    } else if (section.length > 1) {
      if (
        section[0].attributes.question_text ===
        section[1].attributes.question_text
      ) {
        return "sub";
      } else if (
        section[1].attributes.dependent_question_id &&
        section[1].attributes.dependent_question_id.length > 0
      ) {
        return "dependent";
      }
    }
  } catch (e) {
    console.error(e);
    throw new Error("Error in getQuestionSectionType");
  }
};

export const getScoreQuestionLength = (currentScoreQuestion: any) => {
  let currentScoreQuestionLen = 0;
  for (let i = 0; i < currentScoreQuestion.length; i++) {
    currentScoreQuestionLen += currentScoreQuestion[i].length;
  }
  return currentScoreQuestionLen;
};

export function transformQuestions(sortedArray: any, questionMap: any) {
  // converts the sorted 2D array into a 2D array of question objects. This is the final output.
  return sortedArray.map((innerArray: any) => {
    return innerArray.map((questionId: any) => {
      return questionMap[questionId];
    });
  });
}

const scoreResponseMapping = (inputArray: any) => {
  const responseMap: any = {};

  inputArray.forEach((question: any) => {
    responseMap[question.attributes.question_id] = []; // Initialize with empty selectedOption array
  });

  return responseMap;
};

export function processQuestions(inputArray: any) {
  const questionArrayLength = inputArray.length;

  const mappedList = scoreResponseMapping(inputArray);

  const questionMap: any = {};
  inputArray.forEach((item: any) => {
    questionMap[item.attributes.question_id] = item;
  });

  const subQuestions: any[] = [];
  const dependentQuestions: any[] = [];
  const normalQuestions: any[] = [];

  const extractNumber = (id: any) => parseInt(id.split("_")[1], 10);

  // Group Chips (sub-question) questions based on question_text
  const chipsGroups: any = {};
  inputArray.forEach((item: any) => {
    if (item.attributes.option_type === "Chips") {
      const key = item.attributes.question_text;
      if (!chipsGroups[key]) chipsGroups[key] = [];
      chipsGroups[key].push(item);
    }
  });

  // Sort chips groups and split them into groups of 3
  for (const key in chipsGroups) {
    const sortedGroup = chipsGroups[key].sort(
      (a: any, b: any) =>
        extractNumber(a.attributes.question_id) -
        extractNumber(b.attributes.question_id)
    );

    // Split into groups of max 3
    for (let i = 0; i < sortedGroup.length; i += 3) {
      subQuestions.push(
        sortedGroup.slice(i, i + 3).map((q: any) => q.attributes.question_id)
      );
    }
  }

  // Group dependent questions
  const dependentQuestionGroups: any = {};
  inputArray.forEach((item: any) => {
    if (item.attributes.dependent_question_id) {
      const parentId = item.attributes.dependent_question_id;
      if (!dependentQuestionGroups[parentId])
        dependentQuestionGroups[parentId] = [];
      dependentQuestionGroups[parentId].push(item);
    }
  });

  const addedToDependent = new Set();
  for (const parentId in dependentQuestionGroups) {
    if (!questionMap[parentId]) continue; // Ensure parent exists

    const group = [
      questionMap[parentId], // Parent question first
      ...dependentQuestionGroups[parentId].sort(
        (a: any, b: any) =>
          extractNumber(a.attributes.question_id) -
          extractNumber(b.attributes.question_id)
      ),
    ].map((q) => q.attributes.question_id);
    dependentQuestions.push(group);
    group.forEach((id) => addedToDependent.add(id));
  }

  // Identify normal questions (Single_Select, Checkbox_Search)
  inputArray.forEach((item: any) => {
    const id = item.attributes.question_id;
    if (
      !item.attributes.is_sub_question &&
      !addedToDependent.has(id) &&
      !dependentQuestionGroups[id] &&
      item.attributes.option_type !== "Chips"
    ) {
      normalQuestions.push(id);
    }
  });

  normalQuestions.sort((a: any, b: any) => extractNumber(a) - extractNumber(b));

  // Return the formatted result
  const result = {
    sub_question: subQuestions,
    dependent_question: dependentQuestions,
    normal_question: [normalQuestions],
  };

  return { result, questionMap, mappedList, questionArrayLength };
}

export function convertResultTo2DArray(result: any) {
  const sections: string[][] = [];

  // Flatten sections into [{ type, questions }] pairs
  const allSections: { type: string; questions: string[] }[] = [];
  for (const [type, arrays] of Object.entries(result)) {
    (arrays as string[][]).forEach((questions: string[]) => {
      if (questions.length > 0) {
        allSections.push({ type, questions });
      }
    });
  }

  // Check if allSections is empty
  if (allSections.length === 0) {
    return []; // Return empty array if no sections are present
  }

  // Sort sections based on the first question's ID
  allSections.sort((a, b) => {
    const idA = parseInt(a.questions[0].split("_")[1], 10);
    const idB = parseInt(b.questions[0].split("_")[1], 10);
    return idA - idB;
  });

  // Process each section
  for (const section of allSections) {
    const { type, questions } = section;

    // For "sub_question" sections, split if they have more than 3 elements
    if (type === "sub_question" && questions.length > 3) {
      for (let i = 0; i < questions.length; i += 3) {
        sections.push(questions.slice(i, i + 3));
      }
    } else {
      sections.push(questions);
    }
  }

  return sections;
}

// const getCombinedDateTime = (dob: string, time: string) => {
//   if (!dob) return null;

//   const birthDate = new Date(dob);
//   if (isNaN(birthDate.getTime())) return null;

//   // Extract date components from UTC date (preserves the selected date)
//   const year = birthDate.getUTCFullYear();
//   const month = birthDate.getUTCMonth();
//   const day = birthDate.getUTCDate();

//   let hours = 0,
//     minutes = 0;

//   // Parse time if provided
//   if (time && time.trim() !== "") {
//     const timeParts = time.match(/(\d+):(\d+)\s?(AM|PM)?/i);
//     if (timeParts) {
//       hours = parseInt(timeParts[1], 10);
//       minutes = parseInt(timeParts[2], 10);
//       const period = timeParts[3];

//       if (period) {
//         if (period.toUpperCase() === "PM" && hours !== 12) {
//           hours += 12;
//         } else if (period.toUpperCase() === "AM" && hours === 12) {
//           hours = 0;
//         }
//       }
//     }
//   }

//   // CRITICAL FIX: Create datetime directly in UTC
//   // This preserves the exact date/time the user selected
//   const combinedDateTime = new Date(Date.UTC(year, month, day, hours, minutes));

//   return combinedDateTime.toISOString();
// };

const getCombinedDateTime = (dob: string, time: string) => {
  if (!dob) return null;

  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return null;

  // Extract date components from UTC date (preserves the selected date)
  const year = birthDate.getUTCFullYear();
  const month = birthDate.getUTCMonth();
  const day = birthDate.getUTCDate();

  let hours = 0,
    minutes = 0;

  // Parse time if provided
  if (time && time.trim() !== "") {
    const timeParts = time.match(/(\d+):(\d+)\s?(AM|PM)?/i);
    if (timeParts) {
      hours = parseInt(timeParts[1], 10);
      minutes = parseInt(timeParts[2], 10);
      const period = timeParts[3];

      if (period) {
        if (period.toUpperCase() === "PM" && hours !== 12) {
          hours += 12;
        } else if (period.toUpperCase() === "AM" && hours === 12) {
          hours = 0;
        }
      }
    }
  }

  // Convert IST time to UTC time (subtract 5 hours 30 minutes)
  let utcHours = hours - 5;
  let utcMinutes = minutes - 30;

  // Handle minute underflow
  if (utcMinutes < 0) {
    utcMinutes += 60;
    utcHours -= 1;
  }

  // Handle hour underflow
  if (utcHours < 0) {
    utcHours += 24;
  }

  // Create datetime with the SAME date but converted UTC time
  const utcDateTime = new Date();
  utcDateTime.setUTCFullYear(year);
  utcDateTime.setUTCMonth(month);
  utcDateTime.setUTCDate(day);
  utcDateTime.setUTCHours(utcHours, utcMinutes, 0, 0);

  return utcDateTime.toISOString();
};

export const reformatPayload = (
  payload: any,
  userPhoneNumber: string,
  client: string
) => {
  // Helper function to calculate age from ISO date of birth
  const calculateAge = (dob: string) => {
    if (!dob) return null;

    const birthDate = new Date(dob); // Parse the ISO date properly
    if (isNaN(birthDate.getTime())) return null; // Invalid date check

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age >= 0 ? age : null; // Ensure non-negative age
  };

  // Helper function to convert height from feet and inches to centimeters
  const convertHeightToCms = (height: string) => {
    if (!height || height.length === 0) return null;
    return Math.round(parseFloat(height));
  };

  // Helper function to convert waist from inches (string) to centimeters (integer)
  const convertWaistToCms = (waist: string) => {
    if (!waist || waist.length === 0) return null;
    const waistInInches = parseFloat(waist);
    return waistInInches ? Math.round(waistInInches * 2.54) : null;
  };

  // Helper function to convert weight to kilograms as an integer
  const convertWeightToKg = (weight: string) => {
    if (!weight || weight.length === 0) return null;
    const weightInKg = parseFloat(weight);
    return weightInKg ? Math.round(weightInKg) : null;
  };

  // Field mappings
  const fieldsMapping = {
    age: () => calculateAge(payload.dateOfBith),
    disease: () => null,
    email: () => payload.email || null,
    gender: () => payload.sex?.toLowerCase() || null,
    height_in_cms: () => convertHeightToCms(payload.height),
    name: () => payload.fullName || null,
    phone_number: () => {
      const parsed = parsePhoneNumber(userPhoneNumber);
      return parsed.fullPhoneNumber;
    },
    place_of_birth: () => payload.placeOfBirth || null,
    time_of_birth: () =>
      getCombinedDateTime(payload.dateOfBith, payload.timeOfBirth),
    user_id: () => userPhoneNumber,
    waist_circumference_in_cms: () => convertWaistToCms(payload.waist),
    weight_in_kg: () => convertWeightToKg(payload.weight),
    client: () => client,
    emergency_contact_name: () => payload.emergencyContactName,
    emergency_contact_number: () => payload.emergencyContactNumber,
    martial_status: () => payload.maritalStatus,
    current_location: () => payload.currLocation,
    blood_group: () => payload.bloodGroup,
    employment_status: () => payload.employmentStatus || null,
    employment_type: () => payload.employmentType || null,
  };

  // Generate formatted payload
  const formattedPayload = Object.entries(fieldsMapping).reduce(
    (acc, [key, valueFn]) => {
      acc[key] = valueFn();
      return acc;
    },
    {} as Record<string, any>
  );
  return formattedPayload;
};

export const convertWaisttoInches = (
  waistInCm: string | number | undefined
): string => {
  if (!waistInCm) return "";

  // Convert from cm to inches and round to nearest integer
  const waistValue =
    typeof waistInCm === "string" ? parseFloat(waistInCm) : waistInCm;
  if (isNaN(waistValue)) return "";

  const waistInInches = Math.round(waistValue / 2.54);
  return waistInInches.toString();
};

export const getSectionType = (section: any): string => {
  const firstQuestion = section[0]; // First question in the section
  let sectionType = "Normal";
  let secondQuestion = null;
  if (section.length > 1) {
    secondQuestion = section[1]; // Second question in the section
  }

  // 🟢 1️⃣ SubQuestions → If ANY question has option_type: "Chips"
  if (firstQuestion.attributes?.option_type === "Chips") {
    sectionType = "SubQuestions";
  }

  // 🔵 2️⃣ Dependent → If section length > 1 and 1st question's dependent_question_id matches 0th question's question_id
  else if (
    section.length > 1 &&
    secondQuestion &&
    secondQuestion.attributes?.dependent_question_id ===
      firstQuestion.attributes?.question_id
  ) {
    sectionType = "Dependent";
  }

  // 🟠 3️⃣ Normal → If no Chips and no dependent_question_id (null)
  else {
    sectionType = "Normal";
  }

  return sectionType;
};

import { format, startOfWeek, addDays } from "date-fns";
import {
  HealthScoreData,
  HealthScoreGroupItem,
  HealthScoreResult,
  ProcessedAppointment,
} from "../../pages/DashboardNew/interface";
import { VITAL_DEFAULTS } from "../../pages/Onboarding/vitalFieldsConfig";
export const getDatesForCurrentWeek = () => {
  const today = new Date();
  const start = startOfWeek(today, { weekStartsOn: 1 }); // Week starts on Monday
  const dates = Array.from({ length: 7 }, (_, i) =>
    format(addDays(start, i), "dd MMM")
  );
  return dates;
};

export const getTodayDate = () => {
  const today = new Date();
  return format(today, "dd MM");
};

export const checkAndReturnHeader = (header: string) => {
  if (
    header.includes("dashboard") ||
    header.includes("tests") ||
    header.includes("medicines") ||
    header.includes("recommended-tests") ||
    header.includes("appointment") ||
    header.includes("basic-details") ||
    header.includes("login") ||
    header.includes("symptom-form")
  )
    return "";
  else if (header.includes("vikriti-analysis")) return "Vikriti Analysis";
  else if (header.includes("tongue-analysis")) return "Tongue Analysis";
  else if (header.includes("lab-reports")) return "Lab Reports";
  else if (header.includes("astral-health")) return "Astral Health";
  else if (header.includes("health-scores")) return "Health Scores";
  else if (header.includes("score-result")) return header;

  return "";
};
export const showProfileOnHeader = (location: string): boolean => {
  return !location.includes("login");
};

export const getRoundedValue = (
  value: number,
  denominator: number,
  isSubtract: boolean
) => {
  let scoreValue = value;
  let calculatedValue = (scoreValue / denominator) * 100;
  let roundedValue = Math.round(calculatedValue * 10) / 10;
  let finalValue = 100 - roundedValue;
  finalValue = Math.round(finalValue * 100) / 100;
  if (isSubtract) return finalValue;
  else return roundedValue;
};

export const calculateScore = (scoreName: string, scoreValue: number) => {
  switch (scoreName) {
    case "Physical Activity Score":
      return getRoundedValue(scoreValue, 45, true);
    case "Metabolic Score":
      return getRoundedValue(scoreValue, 100, true);
    case "Stress Score":
      return getRoundedValue(scoreValue, 40, false);
    case "Sleep Score":
      return getRoundedValue(scoreValue, 18, false);
    case "Fatty Liver Score":
      return getRoundedValue(scoreValue, 100, false);
    case "Drug Abuse Screening Test":
      return getRoundedValue(scoreValue, 10, false);
    case "Adolescent Substance Abuse Screening":
      return getRoundedValue(scoreValue, 10, false);
    case "Smoking Dependency":
      return getRoundedValue(scoreValue, 12, false);
    case "Asthma Control Test":
      return getRoundedValue(scoreValue, 25, true);
    case "Dyspnea Scale":
      return getRoundedValue(scoreValue, 4, false);
    case "COPD Assessment":
      return getRoundedValue(scoreValue, 40, false);
    case "Sleepiness Scale":
      return getRoundedValue(scoreValue, 24, false);
    case "Insomnia Assessment Index":
      return getRoundedValue(scoreValue, 28, false);
    case "Obstructive Sleep Apnea Assessment":
      return getRoundedValue(scoreValue, 8, false);
    case "Job Stress Scale":
      return getRoundedValue(scoreValue, 40, false);
    case "Dietary Pattern Assessment":
      return getRoundedValue(scoreValue, 50, true);
    case "General Anxiety Score":
      return getRoundedValue(scoreValue, 21, false);
    case "Social Avoidance Scale":
      return getRoundedValue(scoreValue, 30, false);
    case "Mood Disorder Questionnaire":
      return getRoundedValue(scoreValue, 11.02, false);
    case "Patient Health Questionnaire":
      return getRoundedValue(scoreValue, 27, false);
    case "Obsessive Compulsive Inventory":
      return getRoundedValue(scoreValue, 72, false);
    case "PTSD Checklist":
      return getRoundedValue(scoreValue, 80, false);
    case "Internet Addiction Test":
      return getRoundedValue(scoreValue, 100, false);
    case "WHO WellBeing Index (WHO5)":
      return getRoundedValue(scoreValue, 100, true);
    case "Fantastic Lifestyle Checklist":
      return getRoundedValue(scoreValue, 50, true);
    case "Social Health Score":
      return getRoundedValue(scoreValue, 80, false);

    default:
      return scoreValue;
  }
};

export const scoreRiskFactorMapping = (scoreName: string) => {
  switch (scoreName) {
    case "Physical Activity Score":
      return "Tips";
    case "Metabolic Score":
      return "Risk Factors";
    case "Stress Score":
      return "Tips";
    case "Sleep Score":
      return "Benefits";
    case "Fatty Liver Score":
      return "Risk Factors";
    case "Drug Abuse Screening Test":
      return "Symptoms";
    case "Adolescent Substance Abuse Screening":
      return "Symptoms";
    case "Smoking Dependency":
      return "Symptoms";
    case "Asthma Control Test":
      return "Symptoms";
    case "Dyspnea Scale":
      return "Symptoms";
    case "COPD Assessment":
      return "Symptoms";
    case "Sleepiness Scale":
      return "Symptoms";
    case "Insomnia Assessment Index":
      return "Symptoms";
    case "Obstructive Sleep Apnea Assessment":
      return "Symptoms";
    case "Job Stress Scale":
      return "Symptoms";
    case "Dietary Pattern Assessment":
      return "Symptoms";
    case "General Anxiety Score":
      return "Symptoms";
    case "Social Avoidance Scale":
      return "Symptoms";
    case "Mood Disorder Questionnaire":
      return "Symptoms";
    case "Patient Health Questionnaire":
      return "Symptoms";
    case "Obsessive Compulsive Inventory":
      return "Symptoms";
    case "PTSD Checklist":
      return "Symptoms";
    case "Internet Addiction Test":
      return "Symptoms";
    case "WHO WellBeing Index (WHO5)":
      return "Symptoms";
    case "Fantastic Lifestyle Checklist":
      return "Symptoms";
    case "Social Health Score":
      return "Symptoms";

    default:
      return "Symptoms";
  }
};

export const getScoreColor = (level: string) => {
  switch (level) {
    case "Severe Level":
    case "Significant Level":
    case "Higher Risk":
    case "Very High Dependence":
    case "High Dependence":
    case "Very Poorly Controlled Asthma":
    case "Severe Impact":
    case "Significant Impact":
    case "Significant Risk":
    case "Considerable Risk":
    case "Severe Daytime Sleepiness":
    case "Severe Insomnia":
    case "High risk for OSA":
    case "Critical Job Stress":
    case "Severe Job Stress":
    case "Poor Dietary Pattern":
    case "Severe Anxiety":
    case "High Social Anxiety":
    case "Increased Susceptibility to Bipolar Disorder":
    case "Moderately Severe Depression":
    case "Severe Depression":
    case "Risk of OCD":
    case "Risk of PTSD":
    case "Severe Internet Addiction":
    case "Poor Well-Being":
    case "Imbalanced Lifestyle":
    case "Below Acceptable Lifestyle":
    case "Elevated Loneliness":
    case "Sedentary":
    case "Poor Sleep Quality":
    case "High Perceived Stress":
    case "High Risk Fatty Liver":
    case "Good Metabolic Score":
    case "High":
      return "rgba(235, 0, 0, 1)";

    case "Moderate Level":
    case "Medium Dependence":
    case "Poorly Controlled Asthma":
    case "Moderate Impact":
    case "Moderate Risk":
    case "Moderate Daytime Sleepiness":
    case "Intermediate risk for OSA":
    case "Moderate Job Stress":
    case "Average Dietary Pattern":
    case "Moderate Anxiety":
    case "Average Social Anxiety":
    case "Moderate Depression":
    case "Moderate Internet Addiction":
    case "Borderline Healthy Lifestyle":
    case "Significant Loneliness":
    case "Fairly Active":
    case "Average Sleep Quality":
    case "Moderate Perceived Stress":
    case "Moderate Risk Fatty Liver":
    case "Medium":
    case "Moderate":
    case "Moderate Insomnia":
      return "rgba(255, 209, 80, 1)";

    case "Low Level":
    case "No Problem Reported":
    case "Low Risk":
    case "Low Dependence":
    case "Very Low Dependence":
    case "Well Controlled":
    case "Mild Impact":
    case "Little to No Impact":
    case "Low Daytime Sleepiness":
    case "Mild Daytime Sleepiness":
    case "Mild Insomnia":
    case "No clinically significant insomnia":
    case "Low risk for OSA":
    case "Fairly Low Job Stress":
    case "Lower Job Stress":
    case "Good Dietary Pattern":
    case "Minimal Anxiety":
    case "Mild Anxiety":
    case "Low Social Anxiety":
    case "No Indication of Bipolar Disorder":
    case "No Signs of Depression":
    case "Mild Depression":
    case "No risk of OCD":
    case "No risk of PTSD":
    case "Normal Level Internet Addiction":
    case "Mild Level Internet Addiction":
    case "Acceptable to Excellent Well-Being":
    case "Good Lifestyle":
    case "Excellent Lifestyle":
    case "Mild Feelings of Loneliness":
    case "Very Active":
    case "Good Sleep Quality":
    case "Low Perceived Stress":
    case "Low Risk Fatty Liver":
    case "Poor Metabolic Score":
    case "Low":
      return "rgba(41, 191, 69, 1)";

    default:
      return "rgba(128, 128, 128, 1)"; // Default color
  }
};

export const getScoreLabelMap = (level: string) => {
  switch (level) {
    case "Severe Level":
    case "Significant Level":
    case "Higher Risk":
    case "Very High Dependence":
    case "High Dependence":
    case "Very Poorly Controlled Asthma":
    case "Severe Impact":
    case "Significant Impact":
    case "Significant Risk":
    case "Considerable Risk":
    case "Severe Daytime Sleepiness":
    case "Severe Insomnia":
    case "High risk for OSA":
    case "Critical Job Stress":
    case "Severe  Job Stress":
    case "Poor Dietary Pattern":
    case "Severe Anxiety":
    case "High Social Anxiety":
    case "Increased Susceptibility to Bipolar Disorder":
    case "Moderately Severe Depression":
    case "Severe Depression":
    case "Risk of OCD":
    case "Risk of PTSD":
    case "Severe Internet Addiction":
    case "Poor Well-Being":
    case "Imbalanced Lifestyle":
    case "Below Acceptable Lifestyle":
    case "Elevated Loneliness":
    case "High":
    case "Severe Job Stress":
      return "High";

    case "Moderate Level":
    case "Medium Dependence":
    case "Poorly Controlled Asthma":
    case "Moderate Impact":
    case "Moderate Risk":
    case "Moderate Daytime Sleepiness":
    case "Intermediate risk for OSA":
    case "Moderate  Job Stress":
    case "Average Dietary Pattern":
    case "Moderate Anxiety":
    case "Average Social Anxiety":
    case "Moderate Depression":
    case "Moderate Internet Addiction":
    case "Borderline Healthy Lifestyle":
    case "Significant Loneliness":
    case "Medium":
    case "Moderate":
    case "Moderate Insomnia":
      return "Medium";

    case "Low Level":
    case "No Problem Reported":
    case "Low Risk":
    case "Low Dependence":
    case "Very Low Dependence":
    case "Well Controlled":
    case "Mild Impact ":
    case "Little to No Impact":
    case "Low Daytime Sleepiness":
    case "Mild Daytime Sleepiness":
    case "Mild Insomnia":
    case "No clinically significant insomnia":
    case "Low risk for OSA":
    case "Fairly Low Job Stress":
    case "Lower Job Stress":
    case "Good Dietary Pattern":
    case "Minimal Anxiety":
    case "Mild Anxiety":
    case "Low Social Anxiety ":
    case "No Indication of Bipolar Disorder":
    case "No Signs of Depression":
    case "Mild Depression":
    case "No risk of OCD":
    case "No risk of PTSD":
    case "Normal Level Internet Addiction":
    case "Mild Level Internet Addiction":
    case "Acceptable to Excellent Well-Being":
    case "Good Lifestyle":
    case "Excellent Lifestyle":
    case "Mild Feelings of Loneliness":
    case "Low":
      return "Low";
  }
};

export const getScoreLabelForEachScoreForColor = (
  scoreName: string,
  scoreResult: any
) => {
  switch (scoreName) {
    case "Physical Activity Score":
    case "Metabolic Score":
    case "Stress Score":
    case "Sleep Score":
    case "Fatty Liver Score":
      return scoreResult.score_label;
  }
  return scoreResult.label;
};

export const prakritiDesciption = (prakriti: string) => {
  switch (prakriti.toLowerCase()) {
    case "vata":
      return "Vata Prakriti in Ayurveda is characterized by qualities of lightness, dryness, coldness, and mobility, reflecting an individual who is energetic, creative, and quick-thinking but may also experience dryness, anxiety, and instability.";

    case "pitta":
      return "Pitta Prakriti in Ayurveda is characterized by qualities of heat, intensity, sharpness, and transformation, reflecting an individual who is focused, determined, and passionate but may also experience irritability, anger, and digestive imbalances.";

    case "kapha":
      return "Kapha Prakriti in Ayurveda refers to a constitution characterized by qualities of stability, strength, and heaviness but may experience weight gain and congestion.";

    case "vata-pitta":
      return "Vata-Pitta Prakriti individuals are energetic, creative, and passionate, yet prone to stress and digestive issues.";

    case "pitta-kapha":
      return "Pitta-Kapha Prakriti tend to be strong, determined, and focused, with a stable, calm nature. They often have good digestion and a robust physical build but may be prone to being overly competitive.";

    case "vata-kapha":
      return "Vata-Kapha Prakriti tend to be adaptable, creative, and calm, yet can also exhibit a tendency toward sluggishness or imbalance when overstressed.";

    case "vata-pitta-kapha":
      return "Vata-Pitta-Kapha Prakriti individuals are dynamic, energetic, and resilient, with a strong ability to adapt, focus, and persevere.";

    default:
      return "Unknown Prakriti type.";
  }
};

export const getPrakritiVikritiIcon = (name: string) => {
  switch (name) {
    case "Vata":
      return VataCopy;

    case "VATA":
      return VataCopy;

    case "Pitta":
      return Pitta;

    case "PITTA":
      return Pitta;

    case "Kapha":
      return Kapha;

    case "KAPHA":
      return Kapha;

    case "Vata-Pitta":
      return VataPitta;

    case "Vatta-Pitta":
      return VataPitta;

    case "Vata-Kapha":
      return VataKapha;

    case "Vata-Pitta-Kapha":
      return VataPittaKapha;

    case "Pitta-Kapha":
      return PittaKapha;
    default:
      return VataPittaKapha;
  }
};

export const compressPDF = async (file: File): Promise<File> => {
  try {
    const existingPdfBytes = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    // Optionally, you can remove unnecessary content or images here.
    const compressedPdfBytes = await pdfDoc.save({ useObjectStreams: false }); // Compress using object streams.

    // Trigger download of the compressed PDF
    const blob = new Blob([compressedPdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);

    /*

    // Create a temporary anchor element to download the file
    const a = document.createElement("a");
    a.href = url;
    a.download = `compressed_${file.name}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Free up memory by revoking the object URL
    URL.revokeObjectURL(url);

    */

    // Return the compressed file as a File object
    return new File([compressedPdfBytes], file.name, { type: file.type });
  } catch (error) {
    console.error("Error compressing PDF:", error);
    return file; // Fallback to original file in case of error.
  }
};

// Enhanced image compression with HEIC support
export const compressImage = async (
  file: File,
  maxWidth: number = 800,
  quality: number = 0.8
): Promise<File> => {
  try {
    console.log("File to be compressed:", file.name, file.size, file.type);

    // Validate file exists and has content
    if (!file || file.size === 0) {
      throw new Error("File is empty or invalid");
    }

    // Check if the file is an image
    if (
      !file.type.startsWith("image/") &&
      !file.name.toLowerCase().match(/\.(heic|heif)$/)
    ) {
      console.warn("File is not a recognized image format");
      return file;
    }

    // Additional validation for common image formats
    const validImageTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/bmp",
      "image/heic",
      "image/heif",
    ];
    const hasValidType =
      validImageTypes.includes(file.type.toLowerCase()) ||
      file.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp|heic|heif)$/);

    if (!hasValidType) {
      console.warn("File type not supported for image processing:", file.type);
      return file;
    }

    // Handle HEIC files by converting them first
    let processedFile = file;
    let isHeicFile =
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      file.name.toLowerCase().endsWith(".heic") ||
      file.name.toLowerCase().endsWith(".heif");

    if (isHeicFile) {
      try {
        console.log("Converting HEIC file...");
        processedFile = await convertHeicToJpeg(file);
        console.log("HEIC file converted successfully");
      } catch (conversionError) {
        console.error("HEIC conversion failed:", conversionError);
        // Return original file if conversion fails
        const errorMessage =
          conversionError instanceof Error
            ? conversionError.message
            : "Unknown error";
        throw new Error(`HEIC conversion failed: ${errorMessage}`);
      }
    }

    // Skip compression for very small files (under 100KB) or if it's the same as original
    if (processedFile.size < 100000) {
      console.log("File is small enough, skipping compression");
      return processedFile;
    }

    const image = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Failed to get canvas context.");
    }

    // Load image with better error handling
    let imageData: HTMLImageElement | null = null;

    try {
      imageData = await new Promise<HTMLImageElement>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error("Image loading timeout after 30 seconds"));
        }, 30000); // 30 second timeout

        // Create object URL for better compatibility
        const url = URL.createObjectURL(processedFile);
        console.log("Loading image from URL for:", processedFile.name);

        // Clean up URL after loading/error
        const cleanup = () => {
          URL.revokeObjectURL(url);
        };

        image.onload = () => {
          clearTimeout(timeoutId);
          cleanup();
          console.log("Image loaded successfully:", processedFile.name);
          resolve(image);
        };

        image.onerror = (error) => {
          clearTimeout(timeoutId);
          cleanup();

          // Handle different types of error events
          let errorMessage = `Failed to load image: ${processedFile.name}`;

          if (error instanceof ErrorEvent) {
            errorMessage += `. Error: ${
              error.message || "Unknown error event"
            }`;
          } else if (error instanceof Event) {
            errorMessage += `. Event type: ${error.type}`;
          } else if (typeof error === "string") {
            errorMessage += `. Error: ${error}`;
          }

          console.error("Error loading image:", processedFile.name, {
            error,
            errorType: typeof error,
            isErrorEvent: error instanceof ErrorEvent,
            isEvent: error instanceof Event,
            fileType: processedFile.type,
            fileName: processedFile.name,
            fileSize: processedFile.size,
          });

          // For any image that can't be loaded, try to return the original file
          // This handles corrupted files, unsupported formats, etc.
          console.warn(
            "Image cannot be loaded for compression, returning original file as-is"
          );
          resolve(null as any);
        };

        // Set the source after setting up event handlers
        image.src = url;
      });
    } catch (imageLoadError) {
      console.error("Image loading failed with error:", imageLoadError);
      console.log(
        "Returning original file without compression due to loading error"
      );
      return file;
    }

    // If image loading failed, return the original file as-is
    if (!imageData) {
      console.log(
        "Image loading failed, returning original file without compression"
      );
      return file;
    }

    // Calculate dimensions
    const originalWidth = imageData.naturalWidth || imageData.width;
    const originalHeight = imageData.naturalHeight || imageData.height;

    console.log(`Original dimensions: ${originalWidth}x${originalHeight}`);

    // Skip compression if image is already smaller than maxWidth
    if (originalWidth <= maxWidth) {
      console.log(
        "Image is already smaller than maxWidth, skipping compression"
      );
      return processedFile;
    }

    // Calculate scale factor
    const scaleFactor = Math.min(1, maxWidth / originalWidth);
    const newWidth = Math.round(originalWidth * scaleFactor);
    const newHeight = Math.round(originalHeight * scaleFactor);

    console.log(
      `New dimensions: ${newWidth}x${newHeight}, Scale factor: ${scaleFactor}`
    );

    // Set canvas dimensions
    canvas.width = newWidth;
    canvas.height = newHeight;

    // Clear canvas and draw image
    ctx.clearRect(0, 0, newWidth, newHeight);
    ctx.drawImage(imageData, 0, 0, newWidth, newHeight);

    // Determine output format
    let outputMimeType = "image/jpeg"; // Default to JPEG
    let outputQuality: number | undefined = quality;

    // Preserve PNG for images that might have transparency (but convert HEIC to JPEG)
    if (processedFile.type === "image/png" && !isHeicFile) {
      outputMimeType = "image/png";
      outputQuality = undefined; // PNG doesn't use quality parameter
    }

    // Convert canvas to blob
    const compressedBlob = await new Promise<Blob | null>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Canvas to blob conversion failed"));
          }
        },
        outputMimeType,
        outputQuality
      );
    });

    if (!compressedBlob) {
      throw new Error("Image compression failed.");
    }

    // Generate appropriate filename
    const originalName = processedFile.name.split(".").slice(0, -1).join(".");
    const extension = outputMimeType === "image/png" ? "png" : "jpg";
    const newFileName = originalName
      ? `${originalName}.${extension}`
      : `compressed_image.${extension}`;

    console.log(`Original size: ${file.size} bytes`);
    console.log(`Processed file size: ${processedFile.size} bytes`);
    console.log(`Compressed size: ${compressedBlob.size} bytes`);
    console.log(
      `Total compression ratio: ${(
        (1 - compressedBlob.size / file.size) *
        100
      ).toFixed(1)}%`
    );

    const finalFile = new File([compressedBlob], newFileName, {
      type: outputMimeType,
    });
    console.log("Compression completed successfully");
    return finalFile;
  } catch (error) {
    console.error("Error compressing image:", error);
    // Return original file as fallback
    console.log("Returning original file due to compression error");
    return file;
  }
};

// Helper function to convert HEIC to JPEG
async function convertHeicToJpeg(heicFile: File): Promise<File> {
  try {
    console.log("Processing HEIC file:", heicFile.name);

    // Option 1: Try to use heic2any if available
    try {
      // Use heic2any directly (no .default needed with regular import)
      const convertedBlob = (await heic2any({
        blob: heicFile,
        toType: "image/jpeg",
        quality: 0.9,
      })) as Blob;

      const convertedFile = new File(
        [convertedBlob],
        heicFile.name.replace(/\.heic$/i, ".jpg"),
        {
          type: "image/jpeg",
        }
      );

      console.log("HEIC conversion successful using heic2any");
      return convertedFile;
    } catch (importError) {
      console.warn(
        "heic2any library not found. Install it with: npm install heic2any"
      );

      // Option 2: Fallback - return original file with warning
      console.warn(
        "HEIC file cannot be processed without heic2any library. File will be uploaded as-is."
      );

      // Return the original file but with a warning
      throw new Error(
        "HEIC files require the heic2any library for processing. Please install it or convert the file to JPG/PNG first."
      );
    }
  } catch (error) {
    console.error("HEIC conversion failed:", error);
    throw error; // Re-throw the error so it can be handled upstream
  }
}

// Utility function to validate file types
export const validateFileType = (
  file: File,
  acceptedTypes: string
): boolean => {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  // Handle HEIC files specifically
  if (fileName.endsWith(".heic") || fileName.endsWith(".heif")) {
    return (
      acceptedTypes.includes("image/") ||
      acceptedTypes.includes(".heic") ||
      acceptedTypes.includes(".heif")
    );
  }

  // Check MIME type
  if (acceptedTypes.includes(fileType)) {
    return true;
  }

  // Check file extensions
  const fileExtension = fileName.substring(fileName.lastIndexOf("."));
  return acceptedTypes.includes(fileExtension);
};

// Utility function to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// export const reformatDatTime = (utcDatetime: string | null | undefined) => {
//   if (!utcDatetime) return null;

//   const utcDate = new Date(utcDatetime);
//   if (isNaN(utcDate.getTime())) return null; // Ensure valid date

//   // Convert to IST (UTC+5:30)
//   const istOffset = 5.5 * 60 * 60 * 1000;
//   const istDate = new Date(utcDate.getTime() + istOffset);

//   return {
//     date: istDate.toISOString().split("T")[0], // YYYY-MM-DD
//     time: istDate.toLocaleTimeString("en-IN", {
//       hour: "2-digit",
//       minute: "2-digit",
//       hour12: true,
//     }), // hh:mm AM/PM format
//   };
// };

export const reformatDatTime = (utcDatetime: string | null | undefined) => {
  if (!utcDatetime) return null;

  const utcDate = new Date(utcDatetime);
  if (isNaN(utcDate.getTime())) return null;

  // Extract the date part directly from the UTC string (before 'T')
  const datePart = utcDatetime.split("T")[0];

  // Convert only the time to IST
  const istTimeString = utcDate.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });

  return {
    date: datePart,
    time: istTimeString,
  };
};

export const convertHeightcmtoftin = (height?: number) => {
  if (!height) return null;

  const totalInches = height / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);

  return `${feet}ft ${inches}in`;
};

export const getFormattedWaist = (waist?: number) => {
  if (!waist) return null;
  return `${waist} cm`;
};

export const getFormattedWeight = (weight?: number) => {
  if (!weight) return null;
  return `${weight} kg`;
};

export const isCompletelyAnsweredScores = (scores: any, score: string) => {
  if (score === "Prakriti") {
    if (
      scores.question_responses &&
      (scores.question_responses.length === scores.questions.length ||
        scores.question_responses.length === 11)
    ) {
      return true;
    } else return false;
  } else if (
    scores.question_responses &&
    scores.question_responses.length === scores.questions.length
  ) {
    return true;
  }
  return false;
};

export const getReviewAnswerFormattedOutput = (scores: any) => {
  let formattedOutput: any[] = [];

  // Helper function to extract numeric part from question_id
  const extractNumericPart = (questionId: string): number => {
    const match = questionId.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0; // Default to 0 if no number found
  };

  // Helper function to sort by question_id numeric part
  const sortByQuestionId = (a: any, b: any) => {
    return (
      extractNumericPart(a.question_id) - extractNumericPart(b.question_id)
    );
  };

  if (scores && Object.keys(scores).length > 0) {
    Object.keys(scores).forEach((key) => {
      const section = scores[key];

      if (section.questions && section.question_responses) {
        if (isCompletelyAnsweredScores(section, key)) {
          // Sort the questions by their question_id number
          const sortedQuestions = [...section.questions].sort(sortByQuestionId);

          const groupedResponses: any = {
            Chips: [],
            Single_Select: [],
            Checkbox_Search: [],
          };

          const chipsGroups: Record<string, any[]> = {};

          sortedQuestions.forEach((question: any) => {
            const response = section.question_responses.find(
              (res: any) => res.question_id === question.question_id
            );
            let responseValue = response
              ? response.response_value
              : "Not Filled";

            if (responseValue === -1) {
              responseValue = "Not Filled";
            }

            if (question.question_type === "Chips") {
              if (!chipsGroups[question.question_text]) {
                chipsGroups[question.question_text] = [];
              }
              chipsGroups[question.question_text].push({
                questions: question,
                question_response:
                  responseValue !== "Not Filled" && responseValue !== null
                    ? question.question_values[responseValue] || "No Response"
                    : "Not Filled",
              });
            } else if (question.question_type === "Checkbox_Search") {
              if (responseValue && typeof responseValue === "string") {
                const selectedIndexes = responseValue.split(",").map(Number);
                responseValue = selectedIndexes
                  .map((index) => question.question_values[index])
                  .join(", ");
              }
              groupedResponses.Checkbox_Search.push([
                { questions: question, question_response: responseValue },
              ]);
            } else if (question.question_type === "Single_Select") {
              responseValue =
                responseValue !== "Not Filled" && responseValue !== null
                  ? question.question_values[responseValue] || "No Response"
                  : "Not Filled";
              groupedResponses.Single_Select.push([
                { questions: question, question_response: responseValue },
              ]);
            }
          });

          // Sort items within each chips group by question_id
          Object.keys(chipsGroups).forEach((groupKey) => {
            const sortedGroup = chipsGroups[groupKey].sort((a, b) =>
              sortByQuestionId(a.questions, b.questions)
            );
            groupedResponses.Chips.push(sortedGroup);
          });

          // Sort Checkbox_Search and Single_Select groups
          groupedResponses.Checkbox_Search.sort((a: any, b: any) =>
            sortByQuestionId(a[0].questions, b[0].questions)
          );

          groupedResponses.Single_Select.sort((a: any, b: any) =>
            sortByQuestionId(a[0].questions, b[0].questions)
          );

          formattedOutput.push({ label: key, ...groupedResponses });
        }
      }
    });
  }

  return formattedOutput;
};
/**
 * Date utility functions to create and validate appointment timestamps
 */

/**
 * Creates a timestamp for a specific date and time
 * @param date JavaScript Date object for the date
 * @param hours Hour (0-23)
 * @param minutes Minutes (0-59)
 * @returns Unix timestamp in milliseconds
 */
export const createTimestamp = (
  date: Date,
  hours: number,
  minutes: number
): number => {
  // Create a new date to avoid modifying the input date
  const timestamp = new Date(date);
  timestamp.setHours(hours, minutes, 0, 0);
  return timestamp.getTime();
};

/**
 * Creates appointment time data with consistent timestamps
 * @param appointmentDate The date of the appointment
 * @param startHour Starting hour (0-23)
 * @param startMinute Starting minute (0-59)
 * @param durationMinutes Duration in minutes
 * @returns Object with appointmentDate, startTime, and endTime timestamps
 */
export const createAppointmentTimestamps = (
  appointmentDate: Date,
  startHour: number,
  startMinute: number,
  durationMinutes: number = 30
) => {
  // Create date for midnight of the appointment day (to keep date consistent)
  const dateMidnight = new Date(appointmentDate);
  dateMidnight.setHours(0, 0, 0, 0);

  // Create start time
  const startTime = createTimestamp(appointmentDate, startHour, startMinute);

  // Create end time (adding duration)
  const endDate = new Date(startTime);
  endDate.setMinutes(endDate.getMinutes() + durationMinutes);
  const endTime = endDate.getTime();

  return {
    appointmentDate: dateMidnight.getTime(),
    startTime,
    endTime,
  };
};

/**
 * Formats a timestamp for display
 * @param timestamp Unix timestamp in milliseconds
 * @returns Formatted date/time string
 */
export const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata", // IST timezone
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Validates appointment timestamps
 * @param data Appointment time data
 * @returns Validation result with formatted dates
 */
export const validateAppointmentTimestamps = (data: {
  appointmentDate: number;
  startTime: number;
  endTime: number;
}) => {
  return {
    appointmentDateFormatted: formatTimestamp(data.appointmentDate),
    startTimeFormatted: formatTimestamp(data.startTime),
    endTimeFormatted: formatTimestamp(data.endTime),
    isValid: data.startTime > Date.now() && data.endTime > data.startTime,
  };
};

/**
 * Extracts just the time part from a timestamp as a string (HH:MM AM/PM)
 */
export const formatTimeOnly = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export const transformDoctorData = (doctors: any[]) => {
  return doctors.map((doctor) => ({
    doctorId: doctor.userId,
    docName: `${doctor.firstName} ${doctor.lastName}`,
    docEmail: doctor.email,
    docContact: doctor.phone,
    docRole: doctor.role,
    teamId: doctor.teamDetails?.Team?.id || null,
    specialization: doctor.teamDetails?.Team?.name || "",
    docImage: doctor.imageUrl || "",
  }));
};

export const epochToISTDate = (epochTime: number) => {
  const date = new Date(epochTime);
  const options: Intl.DateTimeFormatOptions = {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  };
  return date.toLocaleDateString("en-IN", options);
};

// Helper function to convert epoch time to IST time format
export const epochToIST = (epochTime: number) => {
  const date = new Date(epochTime);
  const options: Intl.DateTimeFormatOptions = {
    timeZone: "Asia/Kolkata",
    hour12: true,
    hour: "2-digit",
    minute: "2-digit",
  };
  return date.toLocaleTimeString("en-IN", options);
};

export const extractSymptoms = (symptomsResponse: any) => {
  if (
    symptomsResponse &&
    symptomsResponse.data &&
    symptomsResponse.data.length > 0 &&
    symptomsResponse.data[0].attributes &&
    symptomsResponse.data[0].attributes.symptoms &&
    symptomsResponse.data[0].attributes.symptoms.data
  ) {
    // Extract symptom names from the nested structure
    const symptomsData = symptomsResponse.data[0].attributes.symptoms.data;
    return symptomsData.map((symptom: any) => symptom.attributes.name);
  }
  return ["No symptoms recorded"]; // Default if no symptoms found
};

export const printConsoleLog = (
  message?: string,
  calledFrom?: string,
  otherMessage?: string,
  actualMessage?: any
) => {
  console.log(
    `CalledFrom: ${calledFrom}\nMessage: ${message}\nOtherMessage: ${otherMessage}\nActualMessage:${actualMessage}`
  );
};
// Height conversion functions - FIXED VERSION WITH EDGE CASE HANDLING
export const convertHeightToCm = (value: string, fromUnit: string): number => {
  if (!value || value.trim() === "") return 0;

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return 0;

  switch (fromUnit) {
    case "cm":
      return numValue;
    case "ft_in":
      const feet = Math.floor(numValue);
      const decimalPart = numValue - feet;

      // CRITICAL FIX: Handle decimal properly
      // 5.1 should be 1 inch, 5.10 should be 10 inches
      let inches: number;
      if (decimalPart === 0) {
        inches = 0;
      } else {
        // Convert decimal to string to see actual digits entered
        const valueStr = value.toString();
        const decimalIndex = valueStr.indexOf(".");
        if (decimalIndex !== -1) {
          const decimalDigits = valueStr.substring(decimalIndex + 1);
          if (decimalDigits.length === 1) {
            // Single digit after decimal: 5.1 = 1 inch
            inches = parseInt(decimalDigits);
          } else {
            // Multiple digits: 5.10 = 10 inches, 5.05 = 5 inches
            inches = parseInt(decimalDigits);
          }
        } else {
          inches = 0;
        }
      }

      return (feet * 12 + inches) * 2.54;
    default:
      return numValue;
  }
};

export const convertHeightFromCm = (
  cmValue: number,
  toUnit: string
): string => {
  if (isNaN(cmValue) || cmValue <= 0) return "";

  switch (toUnit) {
    case "cm":
      return Math.round(cmValue).toString();
    case "ft_in":
      const totalInches = cmValue / 2.54;
      let feet = Math.floor(totalInches / 12);
      let inches = Math.round(totalInches % 12);

      // CRITICAL FIX: Handle edge case where inches = 12
      if (inches >= 12) {
        feet += Math.floor(inches / 12);
        inches = inches % 12;
      }

      // CRITICAL FIX: Always format as 2 digits for consistency
      // 1 inch = .01, 10 inches = .10
      if (inches === 0) {
        return `${feet}.00`;
      } else if (inches < 10) {
        return `${feet}.0${inches}`;
      } else {
        return `${feet}.${inches}`;
      }
    default:
      return cmValue.toString();
  }
};

// Waist circumference conversion functions
export const convertWaistToCm = (value: string, fromUnit: string): number => {
  if (!value || value.trim() === "") return 0;

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return 0;

  switch (fromUnit) {
    case "cm":
      return numValue;
    case "inches":
      return numValue * 2.54; // Convert inches to cm
    default:
      return numValue;
  }
};

export const convertWaistFromCm = (cmValue: number, toUnit: string): string => {
  if (isNaN(cmValue) || cmValue <= 0) return "";

  switch (toUnit) {
    case "cm":
      return Math.round(cmValue).toString();
    case "inches":
      return Math.round(cmValue * 0.393701).toString(); // Convert cm to inches
    default:
      return cmValue.toString();
  }
};

// Enhanced validation functions with configurable min/max
export const validateHeight = (
  value: string,
  unit: string,
  minValue?: number,
  maxValue?: number
): string => {
  if (!value || value.trim() === "") return "";

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "Invalid height (only numbers allowed).";

  // Additional validation for ft_in format
  if (unit === "ft_in") {
    const feet = Math.floor(numValue);

    // CRITICAL FIX: Extract inches properly
    const valueStr = value.toString();
    const decimalIndex = valueStr.indexOf(".");
    let inches = 0;

    if (decimalIndex !== -1) {
      const decimalDigits = valueStr.substring(decimalIndex + 1);
      inches = parseInt(decimalDigits) || 0;
    }

    if (inches >= 12) {
      return "Invalid format. Inches should be 0-11. (e.g., 5.01 for 5ft 1inch, 5.11 for 5ft 11inches)";
    }
  }

  try {
    const cmValue = convertHeightToCm(value, unit);

    // Use provided min/max values or defaults
    const minCm = minValue || 115;
    const maxCm = maxValue || 218;

    if (cmValue < minCm || cmValue > maxCm) {
      if (unit === "ft_in") {
        const minFt = convertHeightFromCm(minCm, "ft_in");
        const maxFt = convertHeightFromCm(maxCm, "ft_in");
        return `Invalid height (must be between ${minFt.replace(
          ".",
          "'"
        )} and ${maxFt.replace(".", "'")}).`;
      } else {
        return `Invalid height (must be between ${minCm} and ${maxCm} cm).`;
      }
    }
  } catch (error) {
    return "Invalid height format.";
  }

  return "";
};

export const validateWaist = (
  value: string,
  unit: string,
  minValue?: number,
  maxValue?: number
): string => {
  if (!value || value.trim() === "") return "";

  const numValue = parseFloat(value);
  if (isNaN(numValue))
    return "Invalid waist circumference (only numbers allowed).";

  try {
    // Use provided min/max values or defaults based on unit
    let minVal, maxVal;

    if (unit === "cm") {
      minVal = VITAL_DEFAULTS.waist.cm.minValue;
      maxVal = VITAL_DEFAULTS.waist.cm.maxValue;
      if (numValue < minVal || numValue > maxVal) {
        return `Invalid waist circumference (must be between ${minVal} and ${maxVal} cm).`;
      }
    } else if (unit === "inches") {
      minVal = VITAL_DEFAULTS.waist.inches.minValue;
      maxVal = VITAL_DEFAULTS.waist.inches.maxValue;
      if (numValue < minVal || numValue > maxVal) {
        return `Invalid waist circumference (must be between ${minVal} and ${maxVal} inches).`;
      }
    }
  } catch (error) {
    return "Invalid waist circumference format.";
  }

  return "";
};

// Enhanced weight validation function
export const validateWeight = (
  value: string,
  unit: string,
  minValue?: number,
  maxValue?: number
): string => {
  if (!value || value.trim() === "") return "";

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "Invalid weight (only numbers allowed).";

  // Convert to kg for validation if needed
  let kgValue = numValue;
  if (unit === "lbs") {
    kgValue = numValue * 0.453592;
  }

  // Use provided min/max values or defaults (in kg)
  const minKg = minValue || 25;
  const maxKg = maxValue || 225;

  if (unit === "lbs") {
    const minLbs = Math.round(minKg * 2.20462);
    const maxLbs = Math.round(maxKg * 2.20462);
    if (numValue < minLbs || numValue > maxLbs) {
      return `Invalid weight (must be between ${minLbs} and ${maxLbs} lbs).`;
    }
  } else {
    if (kgValue < minKg || kgValue > maxKg) {
      return `Invalid weight (must be between ${minKg} and ${maxKg} kg).`;
    }
  }

  return "";
};

// Helper function to safely parse numbers and handle empty values
export const safeParseFloat = (value: string): number => {
  if (!value || value.trim() === "") return 0;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

// Helper function to format height for display
export const formatHeightForDisplay = (value: string, unit: string): string => {
  if (!value || value.trim() === "") return "";

  if (unit === "ft_in") {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      const feet = Math.floor(numValue);

      // Extract inches properly
      const valueStr = value.toString();
      const decimalIndex = valueStr.indexOf(".");
      let inches = 0;

      if (decimalIndex !== -1) {
        const decimalDigits = valueStr.substring(decimalIndex + 1);
        inches = parseInt(decimalDigits) || 0;
      }

      // Handle edge case where inches >= 12
      if (inches >= 12) {
        const additionalFeet = Math.floor(inches / 12);
        inches = inches % 12;
        return `${feet + additionalFeet}ft ${inches}inches`;
      }

      return `${feet}ft ${inches}inches`;
    }
  }

  return value;
};

// Color mapping based on score_label
export const getColorByScoreLabel = (scoreLabel?: string): string => {
  switch (scoreLabel?.toLowerCase()) {
    case "high":
      return "rgba(220, 53, 69, 0.8)"; // Red
    case "medium":
      return "rgba(255, 193, 7, 0.8)"; // Yellow
    case "low":
      return "rgba(40, 167, 69, 0.8)"; // Green
    default:
      return "rgba(108, 117, 125, 0.8)"; // Gray for pending
  }
};

export const processHealthScore = (
  scoreConfig: HealthScoreGroupItem,
  data: HealthScoreData | undefined
): HealthScoreResult => {
  if (!data) {
    return {
      name: scoreConfig.name,
      status: "Pending",
      color: getScoreColor("Medium"), // Default to medium/yellow for pending
      logo: scoreConfig.logo,
    };
  }

  const hasCompleteData =
    data.questions && data.questions.length == data.question_responses?.length;

  return {
    name: scoreConfig.name,
    label: data.label || undefined,
    score_label: data.score_label || undefined,
    score: data.score || undefined,
    max_score: data.max_score || undefined,
    status: hasCompleteData ? "Complete" : "Pending",
    color: hasCompleteData
      ? getScoreColor(data.score_label!)
      : getScoreColor(""),
    logo: scoreConfig.logo,
  };
};

// ADD this helper function at the top of the file, before useDashboardProvider
export const getNextUpcomingAppointments = (
  appointments: ProcessedAppointment[]
): ProcessedAppointment[] => {
  const now = Date.now();

  // Filter upcoming appointments (where start time is in future)
  const upcomingAppointments = appointments.filter((apt) => {
    const startTime =
      apt.appointmentDetails.startTimeRaw < 1000000000000
        ? apt.appointmentDetails.startTimeRaw * 1000
        : apt.appointmentDetails.startTimeRaw;
    return startTime > now;
  });

  if (upcomingAppointments.length === 0) {
    return [];
  }

  // Sort by start time (earliest first)
  const sortedUpcoming = upcomingAppointments.sort((a, b) => {
    const aStartTime =
      a.appointmentDetails.startTimeRaw < 1000000000000
        ? a.appointmentDetails.startTimeRaw * 1000
        : a.appointmentDetails.startTimeRaw;
    const bStartTime =
      b.appointmentDetails.startTimeRaw < 1000000000000
        ? b.appointmentDetails.startTimeRaw * 1000
        : b.appointmentDetails.startTimeRaw;
    return aStartTime - bStartTime;
  });

  // Get the earliest upcoming time
  const nextAppointmentTime =
    sortedUpcoming[0].appointmentDetails.startTimeRaw < 1000000000000
      ? sortedUpcoming[0].appointmentDetails.startTimeRaw * 1000
      : sortedUpcoming[0].appointmentDetails.startTimeRaw;

  // Find all appointments at the same earliest time (for different doctors)
  const nextAppointments = sortedUpcoming.filter((apt) => {
    const aptTime =
      apt.appointmentDetails.startTimeRaw < 1000000000000
        ? apt.appointmentDetails.startTimeRaw * 1000
        : apt.appointmentDetails.startTimeRaw;
    return aptTime === nextAppointmentTime;
  });

  return nextAppointments;
};
